import * as React from 'react'
import { ForwardedRef, FunctionComponent, ReactElement, useState } from 'react'
import { Dialog, DialogSurface, DialogTrigger, makeStyles, Tooltip, TooltipProps } from '@fluentui/react-components'
import { DialogSurfaceProps } from '@fluentui/react-dialog'

export const useDialogStyles = makeStyles({
    dialogHeight: {
        height: '92dvh',
    },
})

export type HammockDialogProps<DialogBodyPropsT = unknown> = {
    key?: string | number

    open?: boolean
    setOpen?: (open: boolean) => void
    onDismiss?: () => void

    surfaceProps?: DialogSurfaceProps

    triggerComponent?: () => ReactElement
    tooltipProps?: Partial<TooltipProps> & Pick<TooltipProps, 'content'>

    dialogBody: FunctionComponent<HammockDialogProps<DialogBodyPropsT>>
    dialogBodyProps?: () => DialogBodyPropsT
}

/**
 * The simplest Dialog we can make. It contains an optional DialogTrigger (+ Tooltip) and a simple DialogSurface. Typing can be anything you like.
 *
 * Forwards its ref to the trigger element, the only DOM node this component renders (needed when used inside e.g. an OverflowItem).
 *
 * @param props all optional props, except dialogBody.
 * @param ref forwarded ref to the trigger element
 */
function HammockDialogInner<DialogBodyPropsT = unknown>(props: HammockDialogProps<DialogBodyPropsT>, ref: ForwardedRef<HTMLElement>): ReactElement {
    const [open, setOpen] = useState(false)

    const trigger = props.triggerComponent?.()

    //We set styles on this Dialog in Base.scss
    return (
        <Dialog open={props.open || open} onOpenChange={(_event, data) => (props.setOpen ? props.setOpen(data.open) : setOpen(data.open))}>
            <DialogTrigger disableButtonEnhancement>
                {trigger && (
                    <Tooltip key={`${props.key}-1`} relationship={'description'} withArrow {...props.tooltipProps}>
                        {React.cloneElement(trigger, { ref } as Partial<unknown>)}
                    </Tooltip>
                )}
            </DialogTrigger>
            <DialogSurface {...props.surfaceProps}>
                {props.dialogBody ? <props.dialogBody onDismiss={() => (props.setOpen ? props.setOpen(false) : setOpen(false))} {...props} /> : <></>}
            </DialogSurface>
        </Dialog>
    )
}

const HammockDialog = React.forwardRef(HammockDialogInner) as <DialogBodyPropsT = unknown>(
    props: HammockDialogProps<DialogBodyPropsT> & { ref?: ForwardedRef<HTMLElement> },
) => ReactElement

export default HammockDialog
